/**
 * RotoTubePage.jsx — internal media library with recent gallery + library browser.
 *
 * Two-pane Finder-like interface:
 * - Left sidebar: navigation (Recent, Library folders by source, Favorites, Collections, Shares)
 * - Right pane: Recently Viewed gallery + selected view (library folder, favorites, etc.)
 *
 * Recently Viewed is tracked in localStorage (per-browser, no sync).
 */

import { useState, useMemo, useEffect } from 'react';
import {
  Box, Typography, TextField, CircularProgress, Button,
  Snackbar, Alert, Menu, MenuItem, ListItemIcon, ListItemText, InputAdornment, IconButton,
  List, ListItemButton, Collapse, Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Favorite as HeartFilledIcon,
  FavoriteBorder as HeartIcon,
  PlaylistAdd as SaveIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Folder as FolderIcon,
  History as HistoryIcon,
  PlaylistPlay as PlaylistIcon,
  Link as ShareLinksIcon,
  Edit as RenameIcon,
  DeleteOutline as DeleteIcon,
  PlaylistRemove as RemoveFromColIcon,
} from '@mui/icons-material';
import {
  useVimeoVideos, useHdphSites, useFrameioProjects, useFrameioFolder,
  useFavorites, useAddFavorite, useRemoveFavorite,
  useCollections, useCollection, useUpdateCollection, useDeleteCollection, useRemoveFromCollection,
  toDescriptor, itemToPlay, itemThumb, mediaKey, canDownload, downloadUrl,
} from '../hooks/useRotoTube';
import { useMobile } from '../hooks/useMobile';
import PlayerModal from '../components/rototube/PlayerModal';
import MediaCard from '../components/rototube/MediaCard';
import ShareDialog from '../components/rototube/ShareDialog';
import AddToCollectionDialog from '../components/rototube/AddToCollectionDialog';
import SharesManager from '../components/rototube/SharesManager';
import { YT } from '../components/rototube/ytTheme';
import { TAB_HEIGHT } from '../components/layout/BottomTabBar';

const STREAM_EMBED = (uid) => `https://iframe.videodelivery.net/${uid}`;
const SOURCE_LABEL = { vimeo: 'Vimeo', hdphotohub: 'Real Estate', frameio: 'Frame.io' };

const descriptorFromItem = (it) => ({
  source: it.source, sourceId: it.sourceId, kind: it.kind, title: it.title,
  thumb: it.thumb, playRef: it.playRef, duration: it.duration,
});
const vmFromItem = (it, extra = {}) => ({
  key: `item:${it.id}`, source: it.source, sourceId: it.sourceId, kind: it.kind,
  thumb: itemThumb(it), title: it.title, subtitle: SOURCE_LABEL[it.source] || '',
  duration: it.duration, play: itemToPlay(it), descriptor: descriptorFromItem(it),
  mediaItemId: it.id, ...extra,
});

// ── Recently viewed (localStorage) ──────────────────────────────
const RECENT_KEY = 'rototube_recent_viewed';
const MAX_RECENT = 12;

function useRecentlyViewed() {
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    try {
      const data = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      setRecent(Array.isArray(data) ? data : []);
    } catch {
      setRecent([]);
    }
  }, []);

  // Stores a serializable card snapshot so any source renders in Recent.
  // Play/thumb URLs are stable backend proxies for Vimeo/HDPhotoHub; Frame.io
  // thumbs are signed and may expire (card hides the broken image gracefully).
  const addRecent = (vm) => {
    setRecent((prev) => {
      const key = `${vm.source}:${vm.sourceId}`;
      const snap = {
        key, source: vm.source, sourceId: vm.sourceId, kind: vm.kind,
        thumb: vm.thumb, title: vm.title, subtitle: vm.subtitle, duration: vm.duration,
        play: vm.play, descriptor: vm.descriptor, timestamp: Date.now(),
      };
      const updated = [snap, ...prev.filter((r) => r.key !== key)].slice(0, MAX_RECENT);
      localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  return { recent, addRecent };
}

// ── Layout atoms ───────────────────────────────────────────────
function CardGrid({ vms, cardCtx }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))', gap: 2 }}>
      {vms.map((vm) => (
        <MediaCard
          key={vm.key} vm={vm}
          favorited={vm.source ? cardCtx.favIndex.has(mediaKey(vm.source, vm.sourceId)) : false}
          onPlay={cardCtx.onPlay} onMenu={cardCtx.onMenu}
        />
      ))}
    </Box>
  );
}

function Centered({ children }) {
  return <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8, color: YT.textSecondary, textAlign: 'center' }}>{children}</Box>;
}
const Loading = () => <Centered><CircularProgress size={28} /></Centered>;
const Empty = ({ msg }) => <Centered><Typography variant="body2">{msg}</Typography></Centered>;

// ── Sidebar ────────────────────────────────────────────────────
function Sidebar({ nav, onNav, expandedSources, onToggleSource }) {
  const { data: collections } = useCollections();
  return (
    <Box sx={{ width: 260, flexShrink: 0, bgcolor: YT.surface, borderRight: `1px solid ${YT.border}`, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
      <List sx={{ p: 0 }}>
        <ListItemButton selected={nav === 'recent'} onClick={() => onNav('recent')} sx={{ color: YT.text, '&.Mui-selected': { bgcolor: YT.chipHover } }}>
          <HistoryIcon sx={{ mr: 1.5, fontSize: 20 }} /> <Typography variant="body2">Recently Viewed</Typography>
        </ListItemButton>
        <Divider sx={{ borderColor: YT.border, my: 0.5 }} />
        <ListItemButton onClick={() => onToggleSource('library')} sx={{ color: YT.text }}>
          <FolderIcon sx={{ mr: 1.5, fontSize: 20 }} />
          <Typography variant="body2">Library</Typography>
          {expandedSources.library ? <CollapseIcon sx={{ ml: 'auto', fontSize: 18 }} /> : <ExpandIcon sx={{ ml: 'auto', fontSize: 18 }} />}
        </ListItemButton>
        <Collapse in={expandedSources.library}>
          <List sx={{ pl: 2, py: 0 }}>
            {['vimeo', 'hdphotohub', 'frameio'].map((src) => (
              <ListItemButton key={src} selected={nav === `library:${src}`} onClick={() => onNav(`library:${src}`)} sx={{ color: YT.text, py: 0.75, '&.Mui-selected': { bgcolor: YT.chipHover } }}>
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{SOURCE_LABEL[src]}</Typography>
              </ListItemButton>
            ))}
          </List>
        </Collapse>
        <Divider sx={{ borderColor: YT.border, my: 0.5 }} />
        <ListItemButton selected={nav === 'favorites'} onClick={() => onNav('favorites')} sx={{ color: YT.text, '&.Mui-selected': { bgcolor: YT.chipHover } }}>
          <HeartFilledIcon sx={{ mr: 1.5, fontSize: 20, color: YT.accent }} /> <Typography variant="body2">Favorites</Typography>
        </ListItemButton>
        <ListItemButton onClick={() => onToggleSource('collections')} sx={{ color: YT.text }}>
          <PlaylistIcon sx={{ mr: 1.5, fontSize: 20 }} />
          <Typography variant="body2">Collections</Typography>
          {expandedSources.collections ? <CollapseIcon sx={{ ml: 'auto', fontSize: 18 }} /> : <ExpandIcon sx={{ ml: 'auto', fontSize: 18 }} />}
        </ListItemButton>
        <Collapse in={expandedSources.collections}>
          <List sx={{ pl: 2, py: 0 }}>
            {(collections || []).map((c) => (
              <ListItemButton key={c.id} selected={nav === `collection:${c.id}`} onClick={() => onNav(`collection:${c.id}`)} sx={{ color: YT.text, py: 0.75, '&.Mui-selected': { bgcolor: YT.chipHover } }}>
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }} noWrap>{c.name}</Typography>
              </ListItemButton>
            ))}
          </List>
        </Collapse>
        <ListItemButton selected={nav === 'shares'} onClick={() => onNav('shares')} sx={{ color: YT.text, '&.Mui-selected': { bgcolor: YT.chipHover } }}>
          <ShareLinksIcon sx={{ mr: 1.5, fontSize: 20 }} /> <Typography variant="body2">Client Links</Typography>
        </ListItemButton>
      </List>
    </Box>
  );
}

// Mobile: the sidebar becomes a horizontal chip row (YouTube category chips).
function MobileNav({ nav, onNav }) {
  const { data: collections } = useCollections();
  const items = [
    { key: 'recent', label: 'Recent' },
    { key: 'library:vimeo', label: 'Vimeo' },
    { key: 'library:hdphotohub', label: 'Real Estate' },
    { key: 'library:frameio', label: 'Frame.io' },
    { key: 'favorites', label: '♥ Favorites' },
    ...(collections || []).map((c) => ({ key: `collection:${c.id}`, label: c.name })),
    { key: 'shares', label: 'Client Links' },
  ];
  return (
    <Box sx={{ display: 'flex', gap: 1, px: 1.5, py: 1.25, overflowX: 'auto', flexShrink: 0, borderBottom: `1px solid ${YT.border}`, '&::-webkit-scrollbar': { display: 'none' } }}>
      {items.map((it) => (
        <Box
          key={it.key} onClick={() => onNav(it.key)}
          sx={{
            px: 1.5, py: 0.85, minHeight: 36, display: 'flex', alignItems: 'center',
            borderRadius: '8px', whiteSpace: 'nowrap', cursor: 'pointer', fontSize: '0.85rem',
            bgcolor: nav === it.key ? YT.chipSelectedBg : YT.chip,
            color: nav === it.key ? YT.chipSelectedText : YT.text,
          }}
        >
          {it.label}
        </Box>
      ))}
    </Box>
  );
}

// ── Library folder view ────────────────────────────────────────
// Mounted with key={source} so per-source drill-in state (HDPhotoHub shoot,
// Frame.io folder stack) resets when switching sources.
function LibraryFolderView({ source, search, cardCtx }) {
  // Call all hooks unconditionally at top level to comply with React hooks rule
  const { data: vimeoData, isError: vimeoError } = useVimeoVideos(source === 'vimeo');
  const { data: hdphData, isError: hdphError } = useHdphSites(source === 'hdphotohub');
  const { data: frameioProjects, isError: fioError } = useFrameioProjects(source === 'frameio');

  // HDPhotoHub drill-down: the open shoot's sid
  const [shoot, setShoot] = useState(null);

  // Frame.io drill-down: stack of { id, name } folders inside a project
  const [fioStack, setFioStack] = useState([]);
  const fioCurrent = fioStack[fioStack.length - 1] || null;
  const { data: fioFolder, isError: fioFolderError } = useFrameioFolder(fioCurrent?.id, source === 'frameio');

  // Sync shoot state if current shoot no longer exists
  useEffect(() => {
    if (source === 'hdphotohub' && shoot && hdphData && !hdphData.find((x) => x.sid === shoot)) {
      setShoot(null);
    }
  }, [shoot, hdphData, source]);

  // Vimeo view
  const vimeoVms = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (vimeoData || []).filter((v) => v.ready && (!q || v.name.toLowerCase().includes(q))).map((v) => ({
      key: `vimeo:${v.id}`, source: 'vimeo', sourceId: String(v.id), kind: 'video-url',
      thumb: v.thumbnail, title: v.name, subtitle: 'Vimeo', duration: v.duration,
      play: { kind: 'video-url', src: `/api/media/vimeo/${v.id}/play`, title: v.name },
      descriptor: toDescriptor('vimeo', v),
    }));
  }, [vimeoData, search]);

  // HDPhotoHub shoots
  const shoots = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (hdphData || []).filter((s) => !q || `${s.address} ${s.city} ${s.state}`.toLowerCase().includes(q));
  }, [hdphData, search]);

  // Render based on source
  if (source === 'vimeo') {
    if (vimeoError) return <Empty msg="Couldn't load Vimeo videos." />;
    if (!vimeoData) return <Loading />;
    return !vimeoVms.length ? <Empty msg={search ? 'No videos match your search.' : 'No Vimeo videos.'} /> : <CardGrid vms={vimeoVms} cardCtx={cardCtx} />;
  }

  if (source === 'hdphotohub') {
    if (hdphError) return <Empty msg="Couldn't load Real Estate shoots." />;
    if (!hdphData) return <Loading />;
    if (shoot) {
      const s = (hdphData || []).find((x) => x.sid === shoot);
      if (!s) return null;
      const q = search.trim().toLowerCase();
      const vms = s.media.filter((m) => !q || (m.name || '').toLowerCase().includes(q)).map((m) => m.type === 'image'
        ? {
          key: `hdph:${m.mid}`, source: 'hdphotohub', sourceId: String(m.mid), kind: 'image',
          thumb: `/api/media/hdphotohub/thumb/${m.mid}`, title: m.name, subtitle: 'Photo',
          play: { kind: 'image', src: m.url, title: m.name }, descriptor: toDescriptor('hdphotohub', m),
        }
        : {
          key: `hdph:${m.mid}`, source: 'hdphotohub', sourceId: String(m.mid), kind: 'stream-iframe',
          thumb: m.thumb, title: m.name, subtitle: 'Video', badge: 'video',
          disabled: !m.streamUid && !m.embedurl,
          play: { kind: 'stream-iframe', src: m.streamUid ? STREAM_EMBED(m.streamUid) : m.embedurl, title: m.name },
          descriptor: toDescriptor('hdphotohub', m),
        });
      return (
        <Box>
          <Button onClick={() => setShoot(null)} sx={{ mb: 2, color: YT.textSecondary, textTransform: 'none' }}>← All shoots</Button>
          <Typography sx={{ mb: 0.5, color: YT.text, fontSize: '1.05rem', fontWeight: 500 }}>{s.address || 'Shoot'}</Typography>
          {!vms.length ? <Empty msg={search ? 'Nothing in this shoot matches.' : s.asleepCount ? `All ${s.asleepCount} photos are asleep — wake them in HDPhotoHub.` : 'This shoot has no media.'} /> : <CardGrid vms={vms} cardCtx={cardCtx} />}
        </Box>
      );
    }
    return !shoots.length ? <Empty msg={search ? 'No shoots match.' : 'No shoots.'} /> : (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))', gap: 2 }}>
        {shoots.map((s) => (
          <MediaCard key={`shoot:${s.sid}`} vm={{ key: `shoot:${s.sid}`, icon: 'photo', thumb: s.cover ? `/api/media/hdphotohub/thumb/${s.cover}` : null, title: s.address || 'Shoot', subtitle: `${[s.city, s.state].filter(Boolean).join(', ')}${s.city || s.state ? ' · ' : ''}${s.photoCount} photos${s.videoCount ? ` · ${s.videoCount} video` : ''}${s.asleepCount ? ` · ${s.asleepCount} asleep` : ''}`, onOpen: () => setShoot(s.sid) }} />
        ))}
      </Box>
    );
  }

  if (source === 'frameio') {
    const q = search.trim().toLowerCase();
    // Inside a folder: subfolders + video files. The back button renders even on
    // a folder load error so the user is never stranded.
    if (fioCurrent) {
      if (!fioFolder && !fioFolderError) return <Loading />;
      const vms = fioFolderError ? [] : [
        ...(fioFolder.folders || []).filter((f) => !q || f.name.toLowerCase().includes(q)).map((f) => ({
          key: `fio-f:${f.id}`, icon: 'folder', title: f.name, subtitle: 'Folder',
          onOpen: () => setFioStack((p) => [...p, { id: f.id, name: f.name }]),
        })),
        ...(fioFolder.files || []).filter((f) => !q || f.name.toLowerCase().includes(q)).map((f) => ({
          key: `fio:${f.id}`, source: 'frameio', sourceId: String(f.id), kind: 'video-url',
          thumb: f.thumbnail, title: f.name, subtitle: 'Frame.io',
          play: { kind: 'video-url', src: `/api/media/frameio/file/${f.id}/play`, title: f.name },
          descriptor: toDescriptor('frameio', f),
        })),
      ];
      return (
        <Box>
          <Button onClick={() => setFioStack((p) => p.slice(0, -1))} sx={{ mb: 2, color: YT.textSecondary, textTransform: 'none' }}>
            ← {fioStack.length > 1 ? fioStack[fioStack.length - 2].name : 'All projects'}
          </Button>
          <Typography sx={{ mb: 0.5, color: YT.text, fontSize: '1.05rem', fontWeight: 500 }}>{fioCurrent.name}</Typography>
          {fioFolderError ? <Empty msg="Couldn't load this Frame.io folder." />
            : !vms.length ? <Empty msg={search ? 'Nothing here matches.' : 'This folder is empty.'} />
              : <CardGrid vms={vms} cardCtx={cardCtx} />}
        </Box>
      );
    }
    // Project list. On reconnect_required, offer the OAuth connect entry point
    // (admin-gated server-side; top-level navigation to the Adobe IMS dance).
    if (fioError) {
      return (
        <Centered>
          <Typography variant="body2">Frame.io isn&apos;t connected.</Typography>
          <Button component="a" href="/api/media/frameio/connect" variant="contained" size="small" sx={{ textTransform: 'none' }}>
            Connect Frame.io
          </Button>
        </Centered>
      );
    }
    if (!frameioProjects) return <Loading />;
    const projects = frameioProjects.filter((p) => !q || p.name.toLowerCase().includes(q));
    return !projects.length ? <Empty msg={search ? 'No projects match.' : 'No Frame.io projects.'} /> : (
      <CardGrid cardCtx={cardCtx} vms={projects.map((p) => ({
        key: `fio-p:${p.id}`, icon: 'folder', title: p.name, subtitle: p.workspace, disabled: !p.rootFolderId,
        onOpen: p.rootFolderId ? () => setFioStack([{ id: p.rootFolderId, name: p.name }]) : undefined,
      }))} />
    );
  }
  return null;
}

// ── Page ───────────────────────────────────────────────────────
export default function RotoTubePage() {
  const { isMobile } = useMobile();
  const [nav, setNav] = useState('recent');
  const [search, setSearch] = useState('');
  const [player, setPlayer] = useState(null);
  const [menu, setMenu] = useState(null);
  const [addToCol, setAddToCol] = useState(null);
  const [share, setShare] = useState(null);
  const [sharesOpen, setSharesOpen] = useState(false);
  const [snack, setSnack] = useState(null);
  const [expandedSources, setExpandedSources] = useState({ library: true, collections: false });

  const { data: favorites } = useFavorites();
  const { recent, addRecent } = useRecentlyViewed();
  const addFavorite = useAddFavorite();
  const removeFavorite = useRemoveFavorite();
  const { data: vimeoData } = useVimeoVideos();

  // Hook called unconditionally; useCollection self-disables when id is null.
  const collectionId = nav.startsWith('collection:') ? nav.split(':')[1] : null;
  const { data: col, isError: colError } = useCollection(collectionId);
  const updateCollection = useUpdateCollection();
  const deleteCollection = useDeleteCollection();
  const removeFromCollection = useRemoveFromCollection();
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState('');

  // Consume ?frameio=connected|error left by the OAuth callback redirect.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const f = sp.get('frameio');
    if (!f) return;
    if (f === 'connected') {
      setSnack({ sev: 'success', msg: 'Frame.io connected' });
      setNav('library:frameio');
    } else {
      setSnack({ sev: 'error', msg: `Frame.io: ${sp.get('msg') || 'connection failed'}` });
    }
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const saveRename = () => {
    const name = renameVal.trim();
    if (name && name !== col?.name) updateCollection.mutate({ id: collectionId, name });
    setRenaming(false);
  };
  const handleDeleteCollection = () => {
    if (!window.confirm(`Delete collection "${col?.name}"? Items stay in the library.`)) return;
    deleteCollection.mutate(collectionId, { onSuccess: () => setNav('recent') });
  };

  const favIndex = useMemo(() => {
    const m = new Map();
    (favorites || []).forEach((it) => { m.set(mediaKey(it.source, it.sourceId), it.id); });
    return m;
  }, [favorites]);

  // Favorites view: memoized VM list
  const favVms = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (favorites || []).filter((it) => !q || (it.title || '').toLowerCase().includes(q)).map((it) => vmFromItem(it));
  }, [favorites, search]);

  // Collection view: memoized VM list (only used if nav.startsWith('collection:'))
  const colVms = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (col?.items || []).filter((it) => !q || (it.title || '').toLowerCase().includes(q)).map((it) => vmFromItem(it));
  }, [col?.items, search]);

  const toggleFav = (vm) => {
    const favId = favIndex.get(mediaKey(vm.source, vm.sourceId));
    if (favId) removeFavorite.mutate(favId);
    else if (vm.descriptor) addFavorite.mutate(vm.descriptor, { onSuccess: () => setSnack({ sev: 'success', msg: 'Added to favorites' }) });
  };

  const cardCtx = {
    onPlay: (vm) => {
      setPlayer(vm);
      if (vm.source && vm.play) addRecent(vm);
    },
    onMenu: (e, vm) => setMenu({ anchorEl: e.currentTarget, vm }),
    favIndex,
  };

  const playerActions = player?.descriptor ? {
    favorited: favIndex.has(mediaKey(player.source, player.sourceId)),
    onFavorite: () => toggleFav(player),
    onAddToCollection: () => setAddToCol({ descriptor: player.descriptor }),
    onShare: () => setShare({ descriptors: [player.descriptor], title: player.title }),
    downloadHref: canDownload(player) ? downloadUrl(player.source, player.sourceId) : null,
  } : null;

  const menuVm = menu?.vm;
  const menuFav = menuVm ? favIndex.has(mediaKey(menuVm.source, menuVm.sourceId)) : false;
  const closeMenu = () => setMenu(null);

  // Render current view
  let viewContent = null;
  if (nav === 'recent') {
    const q = search.trim().toLowerCase();
    const vms = recent
      .map((r) => {
        // Vimeo entries refresh from live data when loaded (fresher thumb/title).
        if (r.source === 'vimeo' && vimeoData) {
          const v = vimeoData.find((x) => String(x.id) === r.sourceId);
          if (v) {
            return {
              key: `recent:${r.key}`, source: 'vimeo', sourceId: String(v.id), kind: 'video-url',
              thumb: v.thumbnail, title: v.name, subtitle: 'Vimeo', duration: v.duration,
              play: { kind: 'video-url', src: `/api/media/vimeo/${v.id}/play`, title: v.name },
              descriptor: toDescriptor('vimeo', v),
            };
          }
        }
        // Other sources (and pre-refresh Vimeo) render from the stored snapshot.
        // Entries from the old format (no play snapshot) are skipped.
        if (!r.play?.src) return null;
        return { ...r, key: `recent:${r.key}` };
      })
      .filter(Boolean)
      .filter((vm) => !q || (vm.title || '').toLowerCase().includes(q));
    viewContent = !vms.length
      ? <Empty msg={search ? 'No recently viewed items match.' : "You haven't viewed any media yet."} />
      : <CardGrid vms={vms} cardCtx={cardCtx} />;
  } else if (nav.startsWith('library:')) {
    const source = nav.split(':')[1];
    viewContent = <LibraryFolderView key={source} source={source} search={search} cardCtx={cardCtx} />;
  } else if (nav === 'favorites') {
    viewContent = !favVms.length
      ? <Empty msg={search ? 'No favorites match your search.' : 'No favorites yet. Use ⋮ on any item to favorite it.'} />
      : <CardGrid vms={favVms} cardCtx={cardCtx} />;
  } else if (nav.startsWith('collection:')) {
    viewContent = colError ? <Empty msg="Couldn't load this collection." />
      : !col ? <Loading />
        : !colVms.length ? <Empty msg={search ? 'Nothing in this collection matches.' : 'This collection is empty.'} />
          : <CardGrid vms={colVms} cardCtx={cardCtx} />;
  } else if (nav === 'shares') {
    viewContent = (
      <Box sx={{ textAlign: 'center', py: 4, color: YT.textSecondary }}>
        <ShareLinksIcon sx={{ fontSize: 48, opacity: 0.5, mb: 2 }} />
        <Typography>Client share links</Typography>
        <Button onClick={() => setSharesOpen(true)} sx={{ mt: 2 }}>Manage</Button>
      </Box>
    );
  }

  return (
    // Negative margin cancels the app shell padding so the dark UI is full-bleed, like YouTube.
    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', bgcolor: YT.bg, mt: isMobile ? -1.5 : -3, mx: isMobile ? -1.5 : -3, mb: isMobile ? 0 : -3, minHeight: '100dvh', color: YT.text }}>
      {/* Navigation: left sidebar on desktop, chip row on mobile */}
      {isMobile
        ? <MobileNav nav={nav} onNav={setNav} />
        : <Sidebar nav={nav} onNav={setNav} expandedSources={expandedSources} onToggleSource={(k) => setExpandedSources((p) => ({ ...p, [k]: !p[k] }))} />}

      {/* Main */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        <Box sx={{ px: isMobile ? 1.5 : 3, py: 2, borderBottom: `1px solid ${YT.border}` }}>
          {collectionId && col ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              {renaming ? (
                <TextField
                  size="small" value={renameVal} autoFocus onChange={(e) => setRenameVal(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setRenaming(false); }}
                  onBlur={saveRename}
                  sx={{ '& .MuiOutlinedInput-root': { color: YT.text, bgcolor: '#121212', '& fieldset': { borderColor: YT.border } } }}
                />
              ) : (
                <Typography variant="h5" sx={{ color: YT.text }}>{col.name}</Typography>
              )}
              <IconButton size="small" onClick={() => { setRenameVal(col.name); setRenaming(true); }} sx={{ color: YT.textSecondary }}><RenameIcon sx={{ fontSize: 18 }} /></IconButton>
              <Button
                size="small" startIcon={<ShareIcon sx={{ fontSize: 16 }} />}
                onClick={() => setShare({ collectionId, title: col.name, itemCount: (col.items || []).length })}
                sx={{ textTransform: 'none', borderRadius: '999px', px: 1.5, bgcolor: YT.chip, color: YT.text, '&:hover': { bgcolor: YT.chipHover } }}
              >
                Share
              </Button>
              <IconButton size="small" onClick={handleDeleteCollection} sx={{ color: YT.textSecondary }}><DeleteIcon sx={{ fontSize: 18 }} /></IconButton>
            </Box>
          ) : (
            <Typography variant="h5" sx={{ color: YT.text, mb: 1 }}>
              {nav === 'recent' ? 'Recently Viewed' : nav === 'favorites' ? 'Favorites' : nav === 'shares' ? 'Client Links' : nav.startsWith('collection:') ? 'Collection' : `Library · ${SOURCE_LABEL[nav.split(':')[1]] || 'Library'}`}
            </Typography>
          )}
          {!nav.includes('share') && (
            <TextField
              size="small" placeholder="Search" value={search} onChange={(e) => setSearch(e.target.value)}
              sx={{ maxWidth: 300, width: '100%', '& .MuiOutlinedInput-root': { color: YT.text, borderRadius: '999px', bgcolor: '#121212', '& fieldset': { borderColor: YT.border } }, '& input::placeholder': { color: YT.textSecondary } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: YT.textSecondary, fontSize: 18 }} /></InputAdornment> }}
            />
          )}
        </Box>
        <Box sx={{ flex: 1, p: isMobile ? 1.5 : 3, pb: isMobile ? `calc(${TAB_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 24px)` : 3, overflowY: 'auto' }}>{viewContent}</Box>
      </Box>

      {/* Player */}
      <PlayerModal item={player?.play} onClose={() => setPlayer(null)} actions={playerActions} />

      {/* Card menu */}
      <Menu anchorEl={menu?.anchorEl} open={Boolean(menu)} onClose={closeMenu} PaperProps={{ sx: { bgcolor: '#282828', color: YT.text } }}>
        {menuVm && [
          <MenuItem key="fav" onClick={() => { toggleFav(menuVm); closeMenu(); }}>
            <ListItemIcon sx={{ color: menuFav ? YT.accent : YT.text }}>{menuFav ? <HeartFilledIcon fontSize="small" /> : <HeartIcon fontSize="small" />}</ListItemIcon>
            <ListItemText>{menuFav ? 'Remove favorite' : 'Favorite'}</ListItemText>
          </MenuItem>,
          <MenuItem key="save" onClick={() => { setAddToCol({ descriptor: menuVm.descriptor }); closeMenu(); }}>
            <ListItemIcon sx={{ color: YT.text }}><SaveIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Save to collection</ListItemText>
          </MenuItem>,
          <MenuItem key="share" onClick={() => { setShare({ descriptors: [menuVm.descriptor], title: menuVm.title }); closeMenu(); }}>
            <ListItemIcon sx={{ color: YT.text }}><ShareIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Share to client</ListItemText>
          </MenuItem>,
          collectionId && menuVm.mediaItemId ? (
            <MenuItem key="rmcol" onClick={() => { removeFromCollection.mutate({ collectionId, itemId: menuVm.mediaItemId }); closeMenu(); }}>
              <ListItemIcon sx={{ color: YT.text }}><RemoveFromColIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Remove from collection</ListItemText>
            </MenuItem>
          ) : null,
          canDownload(menuVm) ? (
            <MenuItem key="dl" component="a" href={downloadUrl(menuVm.source, menuVm.sourceId)} download onClick={closeMenu}>
              <ListItemIcon sx={{ color: YT.text }}><DownloadIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Download</ListItemText>
            </MenuItem>
          ) : null,
        ].filter(Boolean)}
      </Menu>

      {/* Dialogs */}
      {/* key resets the dialog's internal state (added-checkmark, new-name field) per item */}
      <AddToCollectionDialog key={addToCol ? mediaKey(addToCol.descriptor?.source, addToCol.descriptor?.sourceId) : 'closed'} open={Boolean(addToCol)} onClose={() => setAddToCol(null)} descriptor={addToCol?.descriptor} onAdded={(name) => setSnack({ sev: 'success', msg: `Saved to "${name}"` })} />
      <ShareDialog open={Boolean(share)} onClose={() => setShare(null)} payload={share} />
      <SharesManager open={sharesOpen} onClose={() => setSharesOpen(false)} />

      <Snackbar open={Boolean(snack)} autoHideDuration={3500} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack ? <Alert severity={snack.sev} onClose={() => setSnack(null)} variant="filled">{snack.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
